import { checkIsUndefined, StandardObject } from "utils";
import type { SupportedLocale } from "components/supported-language";

export type Pageable<T extends StandardObject> = T & {
  pageSize?: number;
  pageNumber?: number;
};

export interface Sortable {
  property?: string;
  direction?: "ASC" | "DESC";
  orders?: string[];
}

export interface FunctionMetadata {
  urlPrefix: string;
  language?: SupportedLocale;
}

interface AuthenticatedHeader {
  "x-tenant-id": string;
  Authorization: string;
}
interface AuthenticatedFunctionMetadata extends FunctionMetadata {
  options: { headers: AuthenticatedHeader };
}

export type ErrorRespResult = [ret: null, errMsg: string];
export type NoContentRespResult = [ret: null, errMsg: null];
export type NormalSuccessRespResult<T> = [ret: T, errMsg: null];
export type SuccessRespResult<T> =
  | NormalSuccessRespResult<T>
  | NoContentRespResult;
export type HandleRespResult<T> = Promise<
  ErrorRespResult | SuccessRespResult<T>
>;

// export type CashifyFunction<T, R> = (data: T) => Promise<[ret: R | null, errMsg: string | undefined] | undefined>;
export type CashifyFunction<T, R> = (data: T) => HandleRespResult<R>;

export type WrappedCashifyFunction<T extends StandardObject | void, R> = (
  metadata: AuthenticatedFunctionMetadata
) => CashifyFunction<T, R>;

export type WrappedCashifyPageableQuery<
  T extends StandardObject,
  R,
> = WrappedCashifyFunction<Pageable<T>, PaginatedResponse<R>>;

export interface CashifyFunctionWrapper {
  <T extends StandardObject | void, R>(
    func: WrappedCashifyFunction<T, R>
  ): CashifyFunction<T, R>;
}

type ErrorDetail = {
  location: string;
  message: string;
};

export interface CommonResponse {
  status?: string;
  message?: string;
  reason?: string;
  errorCode?: string;
  details?: ErrorDetail[];
}
export interface GenericResponse<T> extends CommonResponse {
  data?: T;
}

export interface PaginatedResponse<T> extends GenericResponse<T[]> {
  pageNumber?: number;
  pageSize?: number;
  totalPages?: number;
  totalSize?: number;
}

export const getErrMsg = (json: CommonResponse): string => {
  let code = json?.errorCode;
  let refNo = json?.message;
  if (!json || (!code && !refNo)) {
    return `Unhandled Error`;
  }
  const codeMap: { [key: string]: string } = {
    CMN00010: "Record not found",
    AUT10001: "Incorrect Username or Password",
    CMN10001: "No changes detected",
    AUT20001: "Incorrect original password",
    AUT30001: "Too frequent to initiate MFA request",
    AUT30003: "Invalid MFA Verification",
  };

  let msg: string = "";
  if (code && codeMap[code]) {
    msg = codeMap[code];
  }
  if (!msg) {
    msg = "Unhandled Error";
    if (refNo) {
      msg = `${msg}. ${refNo}.`;
    } else if (code) {
      msg = `${msg} with error code ${code}.`;
    }
  } else {
    if (refNo) {
      msg = `${msg}. ${refNo}.`;
    }
  }
  return msg;
};

export async function handleResp<T>(resp: Response): HandleRespResult<T> {
  if (resp.status && resp.status === 204) {
    const result: NoContentRespResult = [null, null];
    return result;
  }
  let ret: T;
  const re = /(?<!\d|")([-]?\b\d{15,}\b)(?![\d"])/g;
  try {
    const t = await resp.text();
    // use regex to convert big int to string
    // to prevent number overflow in JS
    // e.g. 12345678901234567890 => "12345678901234567890"
    const replaced = t.replace(re, '"$1"');
    ret = JSON.parse(replaced);
  } catch (err) {
    throw resp.statusText;
  }
  if (resp.status >= 400) {
    let errMsg = getErrMsg(ret as CommonResponse);
    const result: ErrorRespResult = [null, errMsg];
    return result;
  }
  const result = [ret, null] as NormalSuccessRespResult<T>;
  return result;
}

export const handleBlobResp = async (
  resp: Response
): HandleRespResult<Blob> => {
  if (resp.status && resp.status === 204) {
    return [null, ""];
  }
  let ret: Blob;
  try {
    ret = await resp.blob();
  } catch (err) {
    throw resp.statusText;
  }
  if (resp.status >= 400) {
    let errMsg = "Unable to get blob.";
    const result: ErrorRespResult = [null, errMsg];
    return result;
  }
  const result = [ret, null] as NormalSuccessRespResult<Blob>;
  return result;
};

export const handleCashifyBlobResp = async (
  resp: Response
): HandleRespResult<{ blob: Blob; name: string }> => {
  if (resp.status && resp.status === 204) {
    return [null, ""];
  }
  let ret = {} as { blob: Blob; name: string };
  try {
    ret["blob"] = await resp.blob();
  } catch (err) {
    throw resp.statusText;
  }
  const contentDisposition = resp.headers.get("content-disposition");
  if (!!contentDisposition) {
    // assume filename don't contain semicolon
    const name = contentDisposition.split("filename=")[1].split(";")[0];
    if (!!name) {
      ret["name"] = name;
    }
  }
  if (resp.status >= 400) {
    let errMsg = "Unable to get blob.";
    const result: ErrorRespResult = [null, errMsg];
    return result;
  }
  const result = [ret, null] as NormalSuccessRespResult<{
    blob: Blob;
    name: string;
  }>;
  return result;
};

export const getURLWithQueryParams = (
  url: string,
  queryParams: StandardObject,
  emptyStringAsParam: boolean = false
) => {
  if (Object.keys(queryParams).length === 0) {
    return url;
  }
  let queries: string[] = [];
  Object.entries(queryParams)
    .filter(
      ([k, v]) =>
        v === 0 || v === false || (emptyStringAsParam && v === "") || !!v
    )
    .forEach(([k, v]) => {
      if (v.constructor === Array) {
        v.forEach((vv) => {
          queries.push(`${k}=${encodeURIComponent(vv)}`);
        });
      } else {
        queries.push(`${k}=${encodeURIComponent(v)}`);
      }
    });
  return url + "?" + queries.join("&");
};

export const extendFetchOptions = (
  opts: StandardObject,
  extOpts: StandardObject
): StandardObject => {
  let newOpts = { ...opts };
  Object.entries(extOpts).forEach(([k, v]) => {
    if (typeof newOpts[k] === "undefined") {
      newOpts[k] = {};
    }
    newOpts[k] = Object.assign(newOpts[k], v);
  });
  return newOpts;
};

export const getDefaultHeaders = (): StandardObject<string> => ({
  Accept: "application/json",
});

export const validateFields = (fields: any[]) => {
  const hasAllRequiredField: boolean = fields.reduce(
    (val, ret) => ret && !checkIsUndefined(val),
    true
  );
  if (!hasAllRequiredField) {
    return Promise.reject("Invalid field provided.");
  }
  return null;
};
